<template>
  <div class="image-tile">
    <div class="image-tile__preview" :class="{ 'is-filled': value || uploading }" @click="!uploading && pick()">
      <template v-if="uploading">
        <div class="image-tile__loader">
          <span class="image-tile__spinner"></span>
          <div style="font-size: 12px; margin-top: 8px; opacity: 0.7">מעלה תמונה...</div>
        </div>
      </template>
      <template v-else-if="!value">
        <div style="text-align: center; padding: 0 12px">
          <LtIcon name="image" :size="26" />
          <div style="font-size: 12px; margin-top: 6px">{{ hint || 'JPG · PNG' }}</div>
        </div>
      </template>
      <template v-else>
        <img v-if="previewUrl" :src="previewUrl" class="image-tile__img" :alt="title" />
        <div v-else style="font-size: 40px; font-weight: 800; opacity: 0.6">{{ title.charAt(0) }}</div>
        <button type="button" class="replace" @click.stop="pick">
          <LtIcon name="edit" :size="12" /> החלפה
        </button>
      </template>
    </div>
    <div class="image-tile__meta">
      <div>
        <div class="image-tile__name">
          {{ title }}
          <span v-if="required" style="color: var(--c-pink-500)">*</span>
        </div>
        <div class="image-tile__tag muted">{{ hint }}</div>
      </div>
      <button v-if="value" type="button" class="btn btn--link" style="color: var(--c-ink-500)" @click="remove">
        <LtIcon name="trash" :size="14" />
      </button>
    </div>
  </div>
</template>

<script>
import LtIcon from './LtIcon.vue';
import { WIZARD_UPLOAD_KEY } from './wizardInject.js';

export default {
  name: 'ImageTile',
  components: { LtIcon },
  inject: {
    uploadFile: { from: WIZARD_UPLOAD_KEY, default: () => () => Promise.resolve(null) },
  },
  props: {
    value: { type: [Object, Number], default: null },
    title: { type: String, required: true },
    hint: { type: String, default: '' },
    required: { type: Boolean, default: false },
    maxSize: { type: Number, default: 10 * 1024 * 1024 },
    width: { type: Number, default: null },
    height: { type: Number, default: null },
  },
  data() {
    return { uploading: false, localPreview: null };
  },
  computed: {
    previewUrl() {
      if (this.localPreview) return this.localPreview;
      if (this.value && this.value.preview) return this.value.preview;
      return null;
    },
  },
  emits: ['update:value'],
  methods: {
    pick() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowed = ['image/jpeg', 'image/png'];
        if (!allowed.includes(file.type)) {
          this.alert('פורמט לא נתמך. יש להעלות קובץ JPG או PNG בלבד.');
          return;
        }

        if (file.size > this.maxSize) {
          const sizeMB = (file.size / 1024 / 1024).toFixed(1);
          const maxMB = (this.maxSize / 1024 / 1024).toFixed(0);
          this.alert(`הקובץ גדול מדי (${sizeMB}MB). משקל מקסימלי ${maxMB}MB.`);
          return;
        }

        if (this.width && this.height) {
          const objectUrl = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => {
            if (img.naturalWidth !== this.width || img.naturalHeight !== this.height) {
              this.alert(`התמונה חייבת להיות בדיוק ${this.width}×${this.height} פיקסלים. התמונה שלך: ${img.naturalWidth}×${img.naturalHeight}.`);
              URL.revokeObjectURL(objectUrl);
              return;
            }
            this.doUpload(file, objectUrl);
          };
          img.onerror = () => {
            this.alert('לא ניתן לקרוא את התמונה. נסו קובץ אחר.');
            URL.revokeObjectURL(objectUrl);
          };
          img.src = objectUrl;
        } else {
          this.doUpload(file, URL.createObjectURL(file));
        }
      };
      input.click();
    },
    doUpload(file, previewUrl) {
      this.localPreview = previewUrl;
      this.uploading = true;
      this.uploadFile(file, 'image').then((res) => {
        this.uploading = false;
        if (res) this.$emit('update:value', { storage_id: res.storage_id, name: res.name, size: res.size, preview: this.localPreview });
      }).catch(() => {
        this.uploading = false;
      });
    },
    remove() {
      if (this.localPreview) {
        URL.revokeObjectURL(this.localPreview);
        this.localPreview = null;
      }
      this.$emit('update:value', null);
    },
  },
};
</script>
